import { Suspense, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyImport";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { CoreWebVitalsTracker } from "@/components/seo/CoreWebVitalsTracker";
import { trackPageView } from "@/lib/analytics";
import { AuthProvider } from "@/hooks/useAuth";

const Layout = lazyWithRetry(() => import("./components/Layout").then((module) => ({ default: module.Layout })));
const AdminLayout = lazyWithRetry(() => import("./components/admin/AdminLayout").then((module) => ({ default: module.AdminLayout })));
const ProtectedRoute = lazyWithRetry(() => import("./components/admin/ProtectedRoute").then((module) => ({ default: module.ProtectedRoute })));
const Index = lazyWithRetry(() => import("./pages/Index"));
const Works = lazyWithRetry(() => import("./pages/Works"));
const Gallery = lazyWithRetry(() => import("./pages/Gallery"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPostPage = lazyWithRetry(() => import("./pages/BlogPost"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const Dashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const BlogPosts = lazyWithRetry(() => import("./pages/admin/BlogPosts"));
const BlogEditor = lazyWithRetry(() => import("./pages/admin/BlogEditor"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/admin/AdminNewsletter"));
const AdminWorks = lazyWithRetry(() => import("./pages/admin/AdminWorks"));
const AdminGallery = lazyWithRetry(() => import("./pages/admin/AdminGallery"));
const AdminExperiences = lazyWithRetry(() => import("./pages/admin/AdminExperiences"));
const AdminTestimonials = lazyWithRetry(() => import("./pages/admin/AdminTestimonials"));
const AdminComments = lazyWithRetry(() => import("./pages/admin/AdminComments"));
const AdminResearch = lazyWithRetry(() => import("./pages/admin/AdminResearch"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const Analytics = lazyWithRetry(() => import("./pages/admin/Analytics"));
const OnboardingConfirmation = lazyWithRetry(() => import("./pages/OnboardingConfirmation"));
const OnboardingRequests = lazyWithRetry(() => import("./pages/admin/OnboardingRequests"));
const OnboardingRequestDetail = lazyWithRetry(() => import("./pages/admin/OnboardingRequestDetail"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}${location.hash}`);
  }, [location.hash, location.pathname, location.search]);

  return (
    <>
      <CoreWebVitalsTracker />
      <Suspense fallback={<div className="min-h-screen bg-background" aria-hidden="true" />}>
        <Routes>
          {/* Public routes */}
          <Route element={<Layout />}>
            <Route path="/" element={<Index />} />
            <Route path="/works" element={<Works />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/research" element={<Navigate to="/" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/settings" element={<Navigate to="/" replace />} />
            <Route path="/onboarding" element={<Navigate to="/contact?tab=project" replace />} />
            <Route path="/onboarding/confirmation" element={<OnboardingConfirmation />} />
          </Route>

          {/* Auth */}
          <Route path="/auth" element={<Auth />} />

          {/* Protected admin routes */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="blog" element={<BlogPosts />} />
            <Route path="blog/:id" element={<BlogEditor />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="works" element={<AdminWorks />} />
            <Route path="research" element={<AdminResearch />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="experience" element={<AdminExperiences />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="comments" element={<AdminComments />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="onboarding" element={<OnboardingRequests />} />
            <Route path="onboarding/:id" element={<OnboardingRequestDetail />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
